import type { Viewpoint } from "@nav-games/nav-lights";

interface VesselSilhouetteProps {
  vesselId: string;
  viewpoint: Viewpoint;
  /// Visual mode. `night` (default) draws the silhouette dark grey
  /// against a night-sky background — the colour of unlit metal.
  /// `day` draws it solid black against a daytime sky for the
  /// daytime reveal view; lights overlay both.
  mode?: "night" | "day";
}

/// Per-vessel silhouette dispatcher. Maps a vessel ID to one of
/// nine shape families, then renders the front (head-on / stern)
/// or side (port / stbd beam) variant. The side variant is drawn
/// bow-right by default and horizontally flipped for `starboardBeam`
/// so the bow always points toward the visible green sidelight.
///
/// All shapes stay below y≈65 so the lights (which live in y≈12–68)
/// have airspace to occupy. The renderer's job is to draw an
/// iconic vessel that hints at category — sailboat keel, trawler
/// gantry, tug low-aft, hovercraft skirt — not photorealism.
export function VesselSilhouette({
  vesselId,
  viewpoint,
  mode = "night",
}: VesselSilhouetteProps) {
  const template = TEMPLATE_FOR_VESSEL[vesselId] ?? "power";
  const isSide = viewpoint === "portBeam" || viewpoint === "starboardBeam";
  // Bow-right by default; flip horizontally for stbd beam so the
  // bow points to the viewer's left (mirroring the port-beam case,
  // which has the bow on the right).
  const flip = viewpoint === "starboardBeam";
  const transform = flip ? "scale(-1, 1) translate(-100, 0)" : undefined;
  const palette = MODE_PALETTE[mode];

  return (
    <g
      fill={palette.fill}
      stroke={palette.stroke}
      strokeWidth={0.4}
      strokeLinejoin="round"
    >
      <g transform={transform}>
        {isSide
          ? renderSide(template)
          : renderFront(template, viewpoint === "stern", palette)}
      </g>
    </g>
  );
}

type Template =
  | "power"
  | "largePower"
  | "sailboat"
  | "trawler"
  | "workboat"
  | "hovercraft"
  | "pilotBoat"
  | "tug"
  | "barge"
  | "pushingTug"
  | "aground";

const TEMPLATE_FOR_VESSEL: Record<string, Template> = {
  power_under_50m: "power",
  power_over_50m: "largePower",
  sailing: "sailboat",
  sail_with_power: "sailboat",
  anchored_under_50m: "power",
  anchored_over_50m: "largePower",
  fishing_trawling: "trawler",
  fishing_other: "trawler",
  not_under_command: "power",
  restricted_ability: "workboat",
  dredging_underwater: "workboat",
  constrained_by_draft: "largePower",
  pilot_on_duty: "pilotBoat",
  hovercraft: "hovercraft",
  aground: "aground",
  mine_clearance: "workboat",
  towing_long: "tug",
  vessel_being_towed: "barge",
  pushing_ahead: "pushingTug",
};

interface Palette {
  fill: string;
  stroke: string;
  detail: string;
}

const MODE_PALETTE: Record<"night" | "day", Palette> = {
  night: { fill: "#1a2a3a", stroke: "#243549", detail: "#3a4a5a" },
  day: { fill: "#0e1620", stroke: "#000", detail: "#202a35" },
};

function renderFront(template: Template, isStern: boolean, palette: Palette) {
  switch (template) {
    case "power":
      return (
        <>
          {/* Standard hull trapezoid + bridge block. */}
          <polygon points="22,78 78,78 70,68 30,68" />
          <rect x={42} y={56} width={16} height={12} />
          {isStern && <SternBadge palette={palette} />}
        </>
      );
    case "largePower":
      return (
        <>
          {/* Wider, deeper hull + taller multi-deck superstructure. */}
          <polygon points="16,80 84,80 76,66 24,66" />
          <rect x={38} y={50} width={24} height={16} />
          <rect x={44} y={42} width={12} height={8} />
          {isStern && <SternBadge palette={palette} />}
        </>
      );
    case "sailboat":
      return (
        <>
          {/* Narrow hull seen end-on — much slimmer than a power
              vessel, and pointed at the bow/stern. */}
          <polygon points="44,76 56,76 54,72 46,72" />
          {/* Small keel below the waterline */}
          <polygon points="48,76 52,76 50,80" fill={palette.detail} />
          {/* Tall mast — the iconic vertical of a sailboat, drawn
              full-height rather than a stub. */}
          <rect x={49.4} y={20} width={1.2} height={52} fill={palette.detail} />
          {/* Sail hint — a thin triangle off to one side of the
              mast suggesting wind in the canvas. Stays inside
              x=42–58 so it doesn't reach the x=35/65 sidelights. */}
          <polygon
            points="50,26 50,68 58,68"
            fill={palette.detail}
            opacity={0.55}
          />
          {isStern && <SternBadge palette={palette} />}
        </>
      );
    case "trawler":
      return (
        <>
          {/* Power hull with a small aft gantry / derrick implied. */}
          <polygon points="22,78 78,78 70,68 30,68" />
          <rect x={42} y={56} width={16} height={12} />
          {/* A-frame gantry suggestion either side of the bridge. */}
          <line x1={38} y1={68} x2={42} y2={50} stroke={palette.detail} strokeWidth={0.6} />
          <line x1={62} y1={68} x2={58} y2={50} stroke={palette.detail} strokeWidth={0.6} />
          {isStern && <SternBadge palette={palette} />}
        </>
      );
    case "workboat":
      return (
        <>
          {/* Power hull + central A-frame derrick going taller. */}
          <polygon points="20,78 80,78 72,68 28,68" />
          <rect x={40} y={56} width={20} height={12} />
          <line x1={36} y1={68} x2={50} y2={48} stroke={palette.detail} strokeWidth={0.6} />
          <line x1={64} y1={68} x2={50} y2={48} stroke={palette.detail} strokeWidth={0.6} />
          {isStern && <SternBadge palette={palette} />}
        </>
      );
    case "hovercraft":
      return (
        <>
          {/* Distinctive flat hull on a rounded skirt below; bulbous
              cockpit on top. Stays compact so the lights read clean. */}
          <ellipse cx={50} cy={78} rx={32} ry={4} fill={palette.detail} />
          <rect x={20} y={68} width={60} height={8} rx={2} />
          <ellipse cx={50} cy={62} rx={14} ry={6} />
          {isStern && <SternBadge palette={palette} />}
        </>
      );
    case "pilotBoat":
      return (
        <>
          {/* Smaller, lower launch profile. */}
          <polygon points="30,78 70,78 64,70 36,70" />
          <rect x={44} y={62} width={12} height={8} />
          {isStern && <SternBadge palette={palette} />}
        </>
      );
    case "tug":
      return (
        <>
          {/* Stubby hull with low aft and tall foredeck wheelhouse. */}
          <polygon points="26,78 74,78 68,68 32,68" />
          <rect x={40} y={52} width={20} height={16} />
          {/* Bollard/towing post hint on top */}
          <rect x={49} y={48} width={2} height={4} fill={palette.detail} />
          {isStern && <SternBadge palette={palette} />}
        </>
      );
    case "aground":
      return (
        <>
          {/* Hull listing slightly + sand/rocks under the waterline. */}
          <g transform="rotate(8 50 78)">
            <polygon points="22,78 78,78 70,68 30,68" />
            <rect x={42} y={56} width={16} height={12} />
          </g>
          <ellipse cx={50} cy={84} rx={36} ry={4} fill={palette.detail} />
          {isStern && <SternBadge palette={palette} />}
        </>
      );
    case "barge":
      return (
        <>
          {/* Flat low hull — no superstructure, no mast. The
              defining feature of a tow: just a slab on the water. */}
          <rect x={20} y={70} width={60} height={8} rx={1} />
          {isStern && <SternBadge palette={palette} />}
        </>
      );
    case "pushingTug":
      return (
        <>
          {/* Head-on shows the barge in front (mostly blocks the
              tug from view) — a low flat slab with the tug's
              wheelhouse peeking above. */}
          <rect x={18} y={70} width={64} height={8} rx={1} />
          {/* Tug wheelhouse visible behind, just above the barge */}
          <rect x={40} y={56} width={20} height={14} />
          <rect x={46} y={50} width={8} height={6} />
          {isStern && <SternBadge palette={palette} />}
        </>
      );
  }
}

function renderSide(template: Template) {
  switch (template) {
    case "power":
      return (
        <>
          {/* Long low hull, bow to the right. */}
          <polygon points="14,76 78,76 86,70 76,68 22,68" />
          {/* Wheelhouse aft-of-centre */}
          <rect x={52} y={58} width={18} height={10} />
        </>
      );
    case "largePower":
      return (
        <>
          {/* Bigger hull profile + multi-tier superstructure. */}
          <polygon points="10,80 84,80 92,70 80,66 18,66" />
          {/* Superstructure block stacked */}
          <rect x={48} y={50} width={28} height={16} />
          <rect x={58} y={42} width={14} height={8} />
          {/* Bridge wing hint */}
          <rect x={56} y={46} width={18} height={2} fill="currentColor" opacity={0.6} />
        </>
      );
    case "sailboat":
      return (
        <>
          {/* Long thin hull (bow points right). */}
          <polygon points="14,72 80,72 86,68 22,68" />
          {/* Keel hanging below the waterline */}
          <polygon points="40,72 58,72 56,80 42,80" />
          {/* Tall mast running full height from deck to upper sky */}
          <rect x={47.4} y={18} width={1.2} height={50} />
          {/* Boom — horizontal spar running aft (left) from the mast */}
          <line x1={28} y1={62} x2={48} y2={62} stroke="currentColor" strokeWidth={0.7} />
          {/* Mainsail — triangle filling between mast and boom */}
          <polygon points="48,20 48,62 28,62" opacity={0.55} />
          {/* Jib (foresail) — small triangle set forward of the mast */}
          <polygon points="48,22 48,50 66,68" opacity={0.4} />
        </>
      );
    case "trawler":
      return (
        <>
          {/* Power hull + tall aft gantry/A-frame for the trawl warps. */}
          <polygon points="14,76 78,76 86,70 76,68 22,68" />
          <rect x={48} y={58} width={18} height={10} />
          {/* Aft A-frame */}
          <line x1={18} y1={68} x2={30} y2={50} stroke="currentColor" strokeWidth={0.6} />
          <line x1={36} y1={68} x2={30} y2={50} stroke="currentColor" strokeWidth={0.6} />
          <line x1={28} y1={50} x2={32} y2={50} stroke="currentColor" strokeWidth={0.6} />
        </>
      );
    case "workboat":
      return (
        <>
          {/* Power hull + foredeck A-frame derrick. */}
          <polygon points="14,76 78,76 86,70 76,68 22,68" />
          <rect x={20} y={58} width={16} height={10} />
          {/* Foredeck derrick */}
          <line x1={56} y1={68} x2={62} y2={48} stroke="currentColor" strokeWidth={0.6} />
          <line x1={68} y1={68} x2={62} y2={48} stroke="currentColor" strokeWidth={0.6} />
        </>
      );
    case "hovercraft":
      return (
        <>
          {/* Long pancake hull + rounded skirt + small cockpit bump. */}
          <ellipse cx={50} cy={76} rx={40} ry={5} fill="currentColor" opacity={0.8} />
          <rect x={12} y={66} width={76} height={8} rx={3} />
          <ellipse cx={50} cy={62} rx={14} ry={5} />
        </>
      );
    case "pilotBoat":
      return (
        <>
          {/* Small launch. */}
          <polygon points="24,74 72,74 78,70 70,68 30,68" />
          <rect x={48} y={62} width={14} height={6} />
        </>
      );
    case "tug":
      return (
        <>
          {/* Short stubby hull, foredeck wheelhouse tall + low aft
              for the tow line. Bow points right; tow stretches
              away to the left (astern). */}
          <polygon points="36,76 80,76 84,70 78,68 42,68" />
          <rect x={56} y={56} width={18} height={12} />
          {/* Tow line trailing aft to the barge */}
          <line x1={36} y1={70} x2={20} y2={72} stroke="currentColor" strokeWidth={0.5} />
          {/* Barge being towed astern — drawn dimmer so it reads
              as "the tow", with the tug's lights still primary. */}
          <rect x={2} y={72} width={18} height={5} rx={0.5} opacity={0.6} />
        </>
      );
    case "aground":
      return (
        <>
          <g transform="rotate(-7 50 76)">
            <polygon points="14,76 78,76 86,70 76,68 22,68" />
            <rect x={52} y={58} width={18} height={10} />
          </g>
          {/* Sand bar under the hull */}
          <ellipse cx={50} cy={82} rx={44} ry={4} fill="currentColor" opacity={0.5} />
        </>
      );
    case "barge":
      return (
        <>
          {/* Long flat barge — bow points right toward the tug. */}
          <rect x={10} y={68} width={70} height={8} rx={1} />
          {/* Tow line trailing forward off the bow to the tug
              implied beyond the right edge of the frame. */}
          <line
            x1={80}
            y1={70}
            x2={96}
            y2={66}
            stroke="currentColor"
            strokeWidth={0.5}
          />
        </>
      );
    case "pushingTug":
      return (
        <>
          {/* Barge in front (right) being pushed by a tug pressed
              up against its stern (left). No tow line — pushing. */}
          <rect x={40} y={68} width={50} height={8} rx={1} />
          {/* Tug body behind the barge */}
          <polygon points="10,76 42,76 44,70 16,68" />
          {/* Tug wheelhouse rising tall above the barge */}
          <rect x={20} y={54} width={18} height={14} />
          {/* Bollard hint atop the wheelhouse */}
          <rect x={28} y={50} width={2} height={4} fill="currentColor" />
        </>
      );
  }
}

function SternBadge({ palette }: { palette: Palette }) {
  return (
    <text
      x={50}
      y={92}
      fill={palette.detail}
      fontSize={6}
      textAnchor="middle"
      fontFamily="ui-monospace, monospace"
    >
      STERN
    </text>
  );
}
